import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import grandpadEnterpriseLogo from "@/assets/grandpad-enterprise-logo-white.svg";
import bbbBadge from "@/assets/bbb-badge.webp";
import hitrustBadge from "@/assets/hitrust-badge.webp";
import hipaaBadge from "@/assets/hipaa-badge.svg";

function FooterLink({ to, children, className }: { to: string; children: React.ReactNode; className: string }) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    if (location.pathname === to) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  };

  return (
    <Link to={to} onClick={handleClick} className={className}>
      {children}
    </Link>
  );
}

export function Footer() {
  return (
    <footer className="w-full bg-anchor text-sailcloth overflow-hidden">
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-8 md:px-12 lg:px-20 3xl:px-28 4xl:px-32 pt-16">
        {/* Top Section - Logo & Mission */}
        <div className="flex gap-12 mb-12 max-lg:flex-col max-lg:gap-8">
          <div className="flex flex-col">
            <img
              src={grandpadEnterpriseLogo}
              alt="GrandPad Enterprise Edition"
              className="h-10 w-auto object-contain"
              width={384}
              height={97}
            />
          </div>
          <div className="flex-1 max-lg:text-center">
            <h3 className="text-h4 text-sailcloth mb-2">Our Mission</h3>
            <p className="text-body-lg text-sailcloth">
              Improve the lives of millions of seniors by reconnecting them with their families, friends, and
              caregivers.
            </p>
          </div>
        </div>

        {/* Footer Links */}
        <div className="grid grid-cols-2 gap-x-16 gap-y-10 mb-12 lg:flex lg:gap-16 max-sm:grid-cols-1 max-sm:gap-8">
          {/* Solutions */}
          <div>
            <h4 className="text-body font-semibold text-sailcloth mb-4">Solutions</h4>
            <ul className="space-y-3">
              <li>
                <FooterLink
                  to="/solutions"
                  className="text-body-sm text-sailcloth/80 hover:text-sailcloth transition-colors"
                >
                  Custom Solutions
                </FooterLink>
              </li>
              <li>
                <FooterLink
                  to="/grandpad-in-action"
                  className="text-body-sm text-sailcloth/80 hover:text-sailcloth transition-colors"
                >
                  GrandPad in Action
                </FooterLink>
              </li>
            </ul>
          </div>

          {/* Proven Results */}
          <div>
            <h4 className="text-body font-semibold text-sailcloth mb-4">Proven Results</h4>
            <ul className="space-y-3">
              <li>
                <FooterLink
                  to="/simple-secure-engagement"
                  className="text-body-sm text-sailcloth/80 hover:text-sailcloth transition-colors"
                >
                  Simple &amp; Secure Engagement
                </FooterLink>
              </li>
              <li>
                <FooterLink
                  to="/turnkey-enablement"
                  className="text-body-sm text-sailcloth/80 hover:text-sailcloth transition-colors"
                >
                  Turnkey Enablement
                </FooterLink>
              </li>
              <li>
                <FooterLink
                  to="/evidence-based-outcomes"
                  className="text-body-sm text-sailcloth/80 hover:text-sailcloth transition-colors"
                >
                  Evidence-Based Outcomes
                </FooterLink>
              </li>
              <li>
                <FooterLink
                  to="/person-centered-support"
                  className="text-body-sm text-sailcloth/80 hover:text-sailcloth transition-colors"
                >
                  Person-Centered Support
                </FooterLink>
              </li>
            </ul>
          </div>

          {/* About Us */}
          <div>
            <h4 className="text-body font-semibold text-sailcloth mb-4">About Us</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://www.grandpad.net/meet-the-team"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-body-sm text-sailcloth/80 hover:text-sailcloth transition-colors"
                >
                  Leadership
                </a>
              </li>
              <li>
                <a
                  href="https://www.grandpad.net/meet-the-team"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-body-sm text-sailcloth/80 hover:text-sailcloth transition-colors"
                >
                  Grand Advisors
                </a>
              </li>
              <li>
                <a
                  href="https://www.grandpad.net/in-the-news"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-body-sm text-sailcloth/80 hover:text-sailcloth transition-colors"
                >
                  In the News
                </a>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-body font-semibold text-sailcloth mb-4">Resources</h4>
            <ul className="space-y-3">
              <li>
                <FooterLink
                  to="/case-studies"
                  className="text-body-sm text-sailcloth/80 hover:text-sailcloth transition-colors"
                >
                  Case Studies
                </FooterLink>
              </li>
              <li>
                <FooterLink
                  to="/downloads"
                  className="text-body-sm text-sailcloth/80 hover:text-sailcloth transition-colors"
                >
                  Downloads
                </FooterLink>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-start gap-6 pb-12 max-sm:gap-4">
          <img src={bbbBadge} alt="BBB Accredited Business" width={170} height={64} className="h-12 w-auto max-sm:h-10" loading="lazy" decoding="async" />
          <img src={hitrustBadge} alt="HITRUST CSF Certified" width={476} height={150} className="h-12 w-auto max-sm:h-10" loading="lazy" decoding="async" />
          <img src={hipaaBadge} alt="HIPAA Compliant" width={150} height={150} className="h-12 w-auto max-sm:h-10" loading="lazy" decoding="async" />
        </div>

        {/* Copyright & Legal Links */}
        <div className="pb-12 flex flex-wrap items-center justify-between gap-y-2 max-sm:flex-col max-sm:items-start">
          <p className="text-body-sm text-sailcloth">GrandPad © {new Date().getFullYear()}. All rights reserved.</p>
          <div className="flex gap-6 max-sm:gap-4">
            <FooterLink to="/contact-us" className="text-body-sm text-sailcloth hover:underline">
              Contact Us
            </FooterLink>
            <a
              href="https://www.grandpad.net/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-body-sm text-sailcloth hover:underline"
            >
              Privacy Policy
            </a>
            <a
              href="https://www.grandpad.net/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="text-body-sm text-sailcloth hover:underline"
            >
              Terms & Conditions
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
